import { call, put, select, takeLatest, all } from 'redux-saga/effects';

import { heliumReceive, requestAndSet } from 'utils/helpers';
import { opTime } from 'utils/dates';

import { getVisitDateTime } from '../../utils/visitorsHelper';
import { visitsActions } from '../slices/visitsSlice';
import { Visit, Visitor } from '../../types/visitTypes';
import { Location } from '../../types/locationTypes';
import { DATE_TIME_FORMAT } from '../../constants/dates';

interface ListAllVisitResponse {
  data?: Visit[];
}

function* fetchVisitsSaga(
  action: ReturnType<typeof visitsActions.fetchVisits>,
): Generator {
  const {
    orgId,
    locationId: payloadLocationId,
    startDate: payloadStartDate,
    endDate: payloadEndDate,
  } = action.payload;

  try {
    yield put(
      visitsActions.setLoading({ key: 'fetchVisitsLoading', value: true }),
    );
    const state = yield select();

    // Prefer explicit payload values (URL-driven Visitors page) and fall back to
    // Redux for callers that dispatch fetchVisits({ orgId }) only (reports,
    // drawer actions). Using the payload makes the fetch independent of the
    // async global-location load, eliminating the empty-on-refresh race.
    const visitsStartDate =
      payloadStartDate ?? state.getIn(['visits', 'visitsStartDate']);
    const visitsEndDate =
      payloadEndDate ?? state.getIn(['visits', 'visitsEndDate']);
    const globalLocationId =
      payloadLocationId ?? state.getIn(['locations', 'globalLocation', 'id']);
    const selectedVisit = state.getIn(['visits', 'selectedVisit']);
    const selectedVisitor = state.getIn(['visits', 'selectedVisitor']);

    let responseData: Visit[] = [];

    if (globalLocationId === -1) {
      const locations: Location[] =
        state.getIn(['locations', 'locations'])?.toJS?.() ||
        state.getIn(['locations', 'locations']) ||
        [];

      for (const location of locations) {
        const tz = location.timezone || 'UTC';
        // Parse the date string as location timezone, then convert to UTC
        const startDate = opTime
          .tz(visitsStartDate, DATE_TIME_FORMAT, tz)
          .utc()
          .format(DATE_TIME_FORMAT);
        const endDate = opTime
          .tz(visitsEndDate, DATE_TIME_FORMAT, tz)
          .utc()
          .format(DATE_TIME_FORMAT);

        const res = (yield call(heliumReceive, 'listAllVisit', [orgId], {
          queryStringParams: {
            locationId: location.id,
            startDate,
            endDate,
          },
        })) as ListAllVisitResponse;

        if (res?.data) {
          responseData.push(...res.data);
        }
      }
    } else {
      const globalLocation: Location | null =
        state.getIn(['locations', 'globalLocation'])?.toJS?.() ||
        state.getIn(['locations', 'globalLocation']);
      const locationsList: Location[] =
        state.getIn(['locations', 'locations'])?.toJS?.() ||
        state.getIn(['locations', 'locations']) ||
        [];
      const matchedLocation = locationsList.find(
        (loc) => loc.id === globalLocationId,
      );
      const tz = matchedLocation?.timezone || globalLocation?.timezone || 'UTC';

      // Parse the date string as location timezone, then convert to UTC
      const startDate = opTime
        .tz(visitsStartDate, DATE_TIME_FORMAT, tz)
        .utc()
        .format(DATE_TIME_FORMAT);
      const endDate = opTime
        .tz(visitsEndDate, DATE_TIME_FORMAT, tz)
        .utc()
        .format(DATE_TIME_FORMAT);

      const res = (yield call(heliumReceive, 'listAllVisit', [orgId], {
        queryStringParams: {
          locationId: globalLocationId,
          startDate,
          endDate,
        },
      })) as ListAllVisitResponse;
      responseData = res?.data || [];
    }

    // NOTE: Host-based visibility filtering (users without "all visitors"
    // access only see visits they host) is applied by the Visitors page
    // (VisitorsTable) where the React Query scope/identity context is
    // available. It must NOT be applied here because this slice is shared by
    // the report pages (Visitor/Repeat/RedFlag/Host/GuestPass reports), which
    // rely on their own report scopes and must show all fetched visits.
    const filteredAndSorted = (responseData || []).sort((a: Visit, b: Visit) =>
      opTime(getVisitDateTime(b, b.visitStatus.name)).diff(
        opTime(getVisitDateTime(a, a.visitStatus.name)),
      ),
    );

    yield put(visitsActions.setVisits(filteredAndSorted));

    if (selectedVisit) {
      const foundVisit = filteredAndSorted.find(
        (v: Visit) => v.id === selectedVisit.id,
      );
      if (foundVisit) {
        yield put(visitsActions.setSelectedVisit(foundVisit));
        if (selectedVisitor) {
          const foundVisitor = foundVisit.visitors?.find(
            (vis: Visitor) => vis.id === selectedVisitor.id,
          );
          yield put(visitsActions.setSelectedVisitor(foundVisitor || null));
        }
      } else {
        const detailRes = (yield call(heliumReceive, 'describeVisit', [
          orgId,
          selectedVisit.id,
        ])) as { data?: Visit[] };
        const detailVisit = detailRes?.data?.[0] || null;
        yield put(visitsActions.setSelectedVisit(detailVisit));
        // Keep selectedVisitor in sync with the freshly fetched visit so
        // profile edits (e.g. middle name) reflect immediately even when the
        // visit is outside the current listAllVisit range/scope.
        if (selectedVisitor) {
          const foundVisitor = detailVisit?.visitors?.find(
            (vis: Visitor) => vis.id === selectedVisitor.id,
          );
          yield put(visitsActions.setSelectedVisitor(foundVisitor || null));
        }
      }
    }
  } catch (e: unknown) {
    const message =
      typeof e === 'object' && e !== null && 'message' in e
        ? String((e as { message?: unknown }).message || '')
        : '';
    yield put(visitsActions.setError(message));
  } finally {
    yield put(
      visitsActions.setLoading({ key: 'fetchVisitsLoading', value: false }),
    );
  }
}

function* describeVisitSaga(
  action: ReturnType<typeof visitsActions.describeVisit>,
) {
  yield requestAndSet(
    'describeVisit',
    [action.payload.orgId, action.payload.visitId],
    {
      createSetterAction: (res: unknown) =>
        visitsActions.setSelectedVisit(
          ((res as { data?: Visit[] }).data || [])[0] || null,
        ),
      loadingAction: (v: boolean) =>
        visitsActions.setLoading({ key: 'describeVisitLoading', value: v }),
    },
  );
}

function* updateVisitSaga(
  action: ReturnType<typeof visitsActions.updateVisit>,
) {
  yield requestAndSet(
    'updateVisit',
    [action.payload.orgId, action.payload.visitId],
    {
      body: action.payload.visitPayload,
      loadingAction: (v: boolean) =>
        visitsActions.setLoading({ key: 'updateVisitLoading', value: v }),
      *postSuccessAction() {
        yield put(visitsActions.describeVisit(action.payload));
        yield put(visitsActions.fetchVisits({ orgId: action.payload.orgId }));
      },
    },
  );
}

function* fetchVisitNotesSaga(
  action: ReturnType<typeof visitsActions.fetchVisitNotes>,
) {
  yield requestAndSet(
    'getVisitorLog',
    [action.payload.orgId, action.payload.visitId],
    {
      createSetterAction: visitsActions.setNotes,
      loadingAction: (v: boolean) =>
        visitsActions.setLoading({ key: 'fetchVisitNotesLoading', value: v }),
    },
  );
}

function* createVisitNoteSaga(
  action: ReturnType<typeof visitsActions.createVisitNote>,
) {
  yield requestAndSet(
    'insertVisitorLog',
    [action.payload.orgId, action.payload.visitId],
    {
      body: action.payload.visitNote,
      loadingAction: (v: boolean) =>
        visitsActions.setLoading({ key: 'addVisitNoteLoading', value: v }),
      *postSuccessAction() {
        yield put(visitsActions.fetchVisitNotes(action.payload));
      },
    },
  );
}

function* fetchVisitorAgreementsSaga(
  action: ReturnType<typeof visitsActions.fetchVisitorAgreements>,
) {
  const { orgId, visitId, ...params } = action.payload;
  yield requestAndSet('getVisitorAgreement', [orgId, visitId], {
    queryStringParams: params,
    createSetterAction: visitsActions.setAgreements,
    loadingAction: (v: boolean) =>
      visitsActions.setLoading({ key: 'fetchAgreementsLoading', value: v }),
  });
}

function* processVisitorExitSaga(
  action: ReturnType<typeof visitsActions.processVisitorExit>,
) {
  yield requestAndSet(
    'newVisitorExit',
    [action.payload.orgId, action.payload.visitId],
    {
      body: action.payload.exitPayload,
      loadingAction: (v: boolean) =>
        visitsActions.setLoading({
          key: 'processVisitorExitLoading',
          value: v,
        }),
      *postSuccessAction() {
        yield put(visitsActions.describeVisit(action.payload));
        yield put(visitsActions.fetchVisits({ orgId: action.payload.orgId }));
      },
    },
  );
}

function* checkDocumentsSaga(
  action: ReturnType<typeof visitsActions.checkDocuments>,
) {
  yield requestAndSet(
    'getVisitDocumentCheck',
    [action.payload.orgId, action.payload.visitId],
    {
      createSetterAction: visitsActions.setDocumentsCheck,
      loadingAction: (v: boolean) =>
        visitsActions.setLoading({ key: 'checkDocumentsLoading', value: v }),
    },
  );
}

export default function* visitsSaga(): Generator {
  yield all([
    takeLatest(visitsActions.fetchVisits.type, fetchVisitsSaga),
    takeLatest(visitsActions.describeVisit.type, describeVisitSaga),
    takeLatest(visitsActions.updateVisit.type, updateVisitSaga),
    takeLatest(visitsActions.fetchVisitNotes.type, fetchVisitNotesSaga),
    takeLatest(visitsActions.createVisitNote.type, createVisitNoteSaga),
    takeLatest(
      visitsActions.fetchVisitorAgreements.type,
      fetchVisitorAgreementsSaga,
    ),
    takeLatest(visitsActions.processVisitorExit.type, processVisitorExitSaga),
    takeLatest(visitsActions.checkDocuments.type, checkDocumentsSaga),
  ]);
}
